// Profiler page - displays a directory of all club members with search functionality
// Allows users to search for members by name and click to view their profile

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom'; // Component for creating clickable links
import { getFirestore, collection, getDocs } from 'firebase/firestore'; // Firebase database functions
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from './firebase-config'; // Firebase app configuration

const db = getFirestore(app); // Initialize database reference

const Profiler = () => {
  const navigate = useNavigate();
  // State variables to store data and manage UI
  const [users, setUsers] = useState([]); // List of all club members
  const [loading, setLoading] = useState(true); // Whether data is still loading
  const [searchTerm, setSearchTerm] = useState(''); // What the user is searching for

  // Auth guard — redirect to login if not authenticated
  useEffect(() => {
    const auth = getAuth(app);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // This effect runs when the component first loads
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Get all user documents from the database
        const snapshot = await getDocs(collection(db, 'users'));
        // Convert the database documents into a list with user data
        const userList = snapshot.docs.map(doc => ({
          id: doc.id, // The user's unique ID
          ...doc.data(), // Spread all the user's data (name, points, etc.)
        }));

        // Sort members alphabetically by name (case-insensitive)
        userList.sort((a, b) => a.name.localeCompare(b.name, 'no', { sensitivity: 'base' }));
        setUsers(userList); // Store the sorted list
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false); // Stop the loading indicator
      }
    };

    fetchUsers();
  }, []);

  // Filter the list of users based on the search input
  // This shows only users whose names match what the user typed
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Show loading message while fetching data
  if (loading) return <div className="profiler-loading">Laster profiler...</div>;

  return (
    <div className="profiler-container">
      <h1>Profiler</h1>

      {/* 🔍 Search box to find members by name */}
      <div className="profiler-search-wrapper">
        <input
          type="text"
          placeholder="Søk etter navn..." // Search by name placeholder
          className="profiler-search"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)} // Update search as user types
        />
      </div>

      {/* 🧑‍🤝‍🧑 Display the list of members */}
      <div className="profiler-list">
        {filteredUsers.length > 0 ? (
          // Show all matching members as clickable links to their profiles
          filteredUsers.map(user => (
            <Link key={user.id} to={`/profil/${user.id}`} className="profiler-item">
              {user.name}
            </Link>
          ))
        ) : (
          // Show message if no members match the search
          <div className="no-results">Ingen profiler funnet.</div>
        )}
      </div>
    </div>
  );
};

export default Profiler;
